/**
 * A gramática dos fluxos: que passos existem, o que cada um precisa e o que a
 * clínica veria acontecer.
 *
 * **O gatilho é o mesmo das automações.** Não há catálogo novo: os seis de
 * `automacoesDaClinica.ts` continuam sendo os únicos, e é por isso que o
 * alcance de um fluxo é a mesma consulta do alcance de uma automação. Dois
 * catálogos dariam duas respostas para "quantos pacientes isso alcança".
 *
 * A validação vive aqui porque ela é usada em três lugares: quando a pessoa
 * monta o fluxo na tela, quando a IA gera um rascunho e antes de simular. Uma
 * validação por lugar seria três oportunidades de discordar.
 */

import type { GatilhoDeAutomacao } from './tipos';

export type TipoDePasso = 'condicao' | 'acao' | 'agente' | 'espera';
export type OperadorDaCondicao = 'maior' | 'menor' | 'igual' | 'contem';

/**
 * Onde o passo está no desenho.
 *
 * Fica no próprio passo, e não numa tabela de posições ao lado: são o mesmo
 * objeto, e duas listas para o mesmo nó divergiriam na primeira remoção.
 * Ausente, o construtor empilha em coluna — desenho novo não precisa de
 * migração de dado.
 */
export interface PosicaoNoDesenho {
    x?: number;
    y?: number;
}

export interface PassoDeCondicao extends PosicaoNoDesenho {
    id: string;
    tipo: 'condicao';
    campo: string;
    operador: OperadorDaCondicao;
    valor: string;
}

export interface PassoDeAcao extends PosicaoNoDesenho {
    id: string;
    tipo: 'acao';
    /** As mesmas ações que o agente propõe — um catálogo, não dois. */
    acao: 'followup_criar' | 'regua_criar' | 'mensagem';
    campos: Record<string, string>;
}

export interface PassoDeAgente extends PosicaoNoDesenho {
    id: string;
    tipo: 'agente';
    /** O que pedir ao agente sobre cada caso alcançado. */
    instrucao: string;
}

export interface PassoDeEspera extends PosicaoNoDesenho {
    id: string;
    tipo: 'espera';
    dias: number;
}

export type PassoDoFluxo = PassoDeCondicao | PassoDeAcao | PassoDeAgente | PassoDeEspera;

export interface DescricaoDePasso {
    tipo: TipoDePasso;
    rotulo: string;
    descricao: string;
    icone: string;
    /**
     * O passo depende de coisa que esta instalação ainda não tem — canal
     * pareado, agendador. Continua podendo ser desenhado; o que ele não faz é
     * rodar, e a simulação diz isso em vez de fingir.
     */
    bloqueio?: string;
}

export const PASSOS: DescricaoDePasso[] = [
    {
        tipo: 'condicao',
        rotulo: 'Condição',
        descricao: 'Recorta quem o gatilho alcançou: só quem tem telefone, só quem sumiu há mais de 120 dias.',
        icone: 'filter',
    },
    {
        tipo: 'acao',
        rotulo: 'Ação',
        descricao: 'Cria tarefa, monta régua de lembrete ou envia mensagem.',
        icone: 'zap',
    },
    {
        tipo: 'agente',
        rotulo: 'Agente',
        descricao: 'Pede ao agente de atendimento que redija o que dizer, caso a caso.',
        icone: 'bot',
    },
    {
        tipo: 'espera',
        rotulo: 'Esperar',
        descricao: 'Segura o fluxo por alguns dias antes do próximo passo.',
        icone: 'clock',
        bloqueio: 'Esperar dias exige guardar em que passo cada caso está, e os fluxos ainda não guardam: o caso para aqui.',
    },
];

export function descricaoDoPasso(tipo: TipoDePasso): DescricaoDePasso | undefined {
    return PASSOS.find((p) => p.tipo === tipo);
}

export const ACOES_DO_FLUXO: { id: PassoDeAcao['acao']; rotulo: string; campos: string[]; bloqueio?: string }[] = [
    { id: 'followup_criar', rotulo: 'Criar tarefa de follow-up', campos: ['titulo', 'vence_em'] },
    { id: 'regua_criar', rotulo: 'Criar régua de lembrete', campos: ['quando', 'offset_min', 'descricao'] },
    {
        id: 'mensagem',
        rotulo: 'Enviar mensagem',
        campos: ['texto'],
        /* Pela tela só roda o que é interno. A mensagem sai pelo agendador — e
           dizer isso aqui evita que "executar agora" pareça ter mandado. */
        bloqueio: 'Pela tela, a mensagem não sai: quem envia é o agendador, pelo WhatsApp conectado, quando a clínica liga o envio automático em Automações.',
    },
];

export function acaoDoFluxo(id: string) {
    return ACOES_DO_FLUXO.find((a) => a.id === id);
}

export const OPERADORES: { id: OperadorDaCondicao; rotulo: string }[] = [
    { id: 'maior', rotulo: 'maior que' },
    { id: 'menor', rotulo: 'menor que' },
    { id: 'igual', rotulo: 'igual a' },
    { id: 'contem', rotulo: 'contém' },
];

/**
 * Os campos que o gatilho entrega, e sobre os quais a condição pode falar.
 *
 * Sem esta lista a condição viraria texto livre sobre um objeto que ninguém
 * sabe qual é — e o fluxo só falharia na hora de rodar.
 */
export const CAMPOS_POR_GATILHO: Record<GatilhoDeAutomacao, { campo: string; rotulo: string }[]> = {
    'sem-retorno-90d': [
        { campo: 'diasSemRetorno', rotulo: 'Dias sem retorno' },
        { campo: 'nome', rotulo: 'Nome' },
        { campo: 'telefone', rotulo: 'Telefone' },
        { campo: 'ultimoProcedimento', rotulo: 'Último procedimento' },
    ],
    'titulo-vencido': [
        { campo: 'diasDeAtraso', rotulo: 'Dias de atraso' },
        { campo: 'valorReais', rotulo: 'Valor em reais' },
        { campo: 'contraparte', rotulo: 'Contraparte' },
    ],
    'estoque-minimo': [
        { campo: 'nome', rotulo: 'Produto' },
        { campo: 'disponivel', rotulo: 'Disponível' },
        { campo: 'minimo', rotulo: 'Mínimo' },
    ],
    'agendamento-24h': [
        { campo: 'paciente', rotulo: 'Paciente' },
        { campo: 'procedimento', rotulo: 'Procedimento' },
        { campo: 'quando', rotulo: 'Quando' },
    ],
    'agendamento-criado': [],
    'atendimento-concluido': [],
};

export interface ProblemaDoFluxo {
    passo?: string;
    mensagem: string;
}

/**
 * O fluxo é desenhável de volta?
 *
 * Vale para o que a pessoa monta e para o que a IA gera — é o mesmo desenho, e
 * um rascunho gerado que a tela não consegue abrir é pior que nenhum rascunho.
 */
export function validarFluxo(gatilho: string, passos: PassoDoFluxo[]): ProblemaDoFluxo[] {
    const problemas: ProblemaDoFluxo[] = [];

    if (!(gatilho in CAMPOS_POR_GATILHO)) {
        problemas.push({ mensagem: `Gatilho desconhecido: ${gatilho}.` });
    }
    if (passos.length === 0) {
        problemas.push({ mensagem: 'O fluxo precisa de ao menos um passo.' });
    }
    if (!passos.some((p) => p.tipo === 'acao')) {
        /* Fluxo sem ação é fluxo que observa e não faz — e a clínica descobre
           isso depois de esperar por ele. */
        problemas.push({ mensagem: 'Nenhum passo faz alguma coisa: acrescente uma ação.' });
    }

    const campos = CAMPOS_POR_GATILHO[gatilho as GatilhoDeAutomacao] ?? [];
    for (const passo of passos) {
        if (passo.tipo === 'condicao') {
            if (campos.length > 0 && !campos.some((c) => c.campo === passo.campo)) {
                problemas.push({
                    passo: passo.id,
                    mensagem: `O gatilho não entrega o campo "${passo.campo}".`,
                });
            }
            if (!passo.valor.trim()) {
                problemas.push({ passo: passo.id, mensagem: 'A condição está sem valor de comparação.' });
            }
        }
        if (passo.tipo === 'acao') {
            const acao = acaoDoFluxo(passo.acao);
            if (!acao) {
                problemas.push({ passo: passo.id, mensagem: `Ação desconhecida: ${passo.acao}.` });
            } else if (acao.id === 'followup_criar' && !passo.campos.titulo?.trim()) {
                problemas.push({ passo: passo.id, mensagem: 'O follow-up precisa de um título.' });
            } else if (acao.id === 'regua_criar' && (!passo.campos.quando?.trim() || !passo.campos.descricao?.trim())) {
                problemas.push({ passo: passo.id, mensagem: 'A régua precisa de rótulo e texto.' });
            } else if (acao.id === 'mensagem' && !passo.campos.texto?.trim()) {
                problemas.push({ passo: passo.id, mensagem: 'A mensagem está vazia.' });
            }
        }
        if (passo.tipo === 'agente' && !passo.instrucao.trim()) {
            problemas.push({ passo: passo.id, mensagem: 'Diga ao agente o que fazer com cada caso.' });
        }
        if (passo.tipo === 'espera' && (!Number.isFinite(passo.dias) || passo.dias <= 0)) {
            problemas.push({ passo: passo.id, mensagem: 'A espera precisa de um número de dias maior que zero.' });
        }
    }

    return problemas;
}

/** Compara um campo do caso com o valor da condição. Número quando os dois são. */
export function condicaoPassa(caso: Record<string, unknown>, passo: PassoDeCondicao): boolean {
    const valorDoCaso = caso[passo.campo];
    const numeroDoCaso = Number(valorDoCaso);
    const numeroDaCondicao = Number(passo.valor);
    const comparaNumero = Number.isFinite(numeroDoCaso) && Number.isFinite(numeroDaCondicao);

    if (passo.operador === 'maior') return comparaNumero && numeroDoCaso > numeroDaCondicao;
    if (passo.operador === 'menor') return comparaNumero && numeroDoCaso < numeroDaCondicao;
    if (passo.operador === 'igual') {
        return comparaNumero
            ? numeroDoCaso === numeroDaCondicao
            : String(valorDoCaso ?? '').toLowerCase() === passo.valor.toLowerCase();
    }
    return String(valorDoCaso ?? '').toLowerCase().includes(passo.valor.toLowerCase());
}

/* ------------------------------------------------------------- o desenho ---
 *
 * O canvas dá posição livre e ligação arrastada; o que EXECUTA continua sendo
 * uma sequência. Por isso a corrente nunca se rompe: ligar A em B move B para
 * logo depois de A, e remover um nó religa os vizinhos. Um passo solto não é
 * fluxo, é rascunho perdido — e o motor não teria como saber quando rodá-lo.
 */

export const ID_DO_GATILHO = 'gatilho';

/** Move `passo` para logo depois de `depoisDe`. `depoisDe` nulo o põe na frente. */
export function religar(passos: PassoDoFluxo[], passo: string, depoisDe: string | null): PassoDoFluxo[] {
    const movido = passos.find((p) => p.id === passo);
    if (!movido || passo === depoisDe) return passos;

    const restantes = passos.filter((p) => p.id !== passo);
    if (depoisDe === null || depoisDe === ID_DO_GATILHO) return [movido, ...restantes];

    const indice = restantes.findIndex((p) => p.id === depoisDe);
    if (indice < 0) return passos;
    return [...restantes.slice(0, indice + 1), movido, ...restantes.slice(indice + 1)];
}

/** As ligações que o desenho mostra: a própria ordem, do gatilho ao último. */
export function ligacoesDaOrdem(passos: PassoDoFluxo[]): { id: string; de: string; para: string }[] {
    const nos = [ID_DO_GATILHO, ...passos.map((p) => p.id)];
    return nos.slice(0, -1).map((de, i) => {
        const para = nos[i + 1] as string;
        return { id: `${de}->${para}`, de, para };
    });
}

/** Onde desenhar cada passo. Sem posição salva, empilha em coluna. */
export function posicaoDoPasso(passo: PassoDoFluxo, indice: number): { x: number; y: number } {
    return {
        x: typeof passo.x === 'number' ? passo.x : 40,
        y: typeof passo.y === 'number' ? passo.y : 140 + indice * 120,
    };
}

/** Uma linha curta dizendo o que o passo faz — é o que o nó mostra no desenho. */
export function resumoDoPasso(passo: PassoDoFluxo): string {
    if (passo.tipo === 'condicao') {
        const operador = OPERADORES.find((o) => o.id === passo.operador)?.rotulo ?? passo.operador;
        return `${passo.campo} ${operador} ${passo.valor || '—'}`;
    }
    if (passo.tipo === 'acao') {
        const acao = acaoDoFluxo(passo.acao);
        const primeiro = Object.values(passo.campos).find((v) => v?.trim());
        return primeiro ? `${acao?.rotulo}: ${primeiro}` : (acao?.rotulo ?? passo.acao);
    }
    if (passo.tipo === 'agente') return passo.instrucao || 'sem instrução';
    return `${passo.dias} ${passo.dias === 1 ? 'dia' : 'dias'}`;
}
